import { FC, useEffect, useState } from 'react';
import Link from 'next/link';
import cn from 'classnames';
import { Menu } from '@headlessui/react';
import { MenuIcon, RingbellIcon, QuestionMarkIcon } from '~/svgs';
import { NextLink, Translate, useTranslate } from '~/i18n';
import { useAccountStatus, useAuth } from '~/auth';
import { Arrow } from '~/components/common/arrow';
import { LangSelector } from '~/components/common/lang-selector';
import NotificationItem from '~/components/notifications/notification-item';
import { Button } from '~/components/common/button';
import { useLayout } from './provider';
import useMedia from '~/hooks/use-media';
import useNotificationList from '~/apis/notification/notifications-list';
import QuickstartGuideModal from '~/components/tutorials/quickstart-guide-modal';
import UpdateProfileTypeModal from '../incomplete-dashboard/onboard/update-profile-type-modal';
import useBnplStatus from '~/apis/bnpl/useBnplStatus';
import BnplSetupModal from '~/components/bnpl/bnpl-setup-modal';
import BnplOtpModal from '~/components/bnpl/bnpl-otp-modal';

export const Subheader = () => {
   const { toggleMenu } = useLayout();
   const { logout, user } = useAuth();
   const [showQuickstart, setShowQuickstart] = useState(false);
   const { lang } = useTranslate();
   const [openProfileTypeModal, setOpenProfileTypeModal] = useState(false);

   const [showBnplPopup, setShowBnplPopup] = useState(false);
   const [showVerifyPopup, setShowVerifyPopup] = useState(false);
   const { data: bnplStatusData, refetch: refetchBnplStatus } = useBnplStatus();
   const [tamara, setTamara] = useState<boolean>(false);
   const [tabby, setTabby] = useState<boolean>(false);
   useEffect(() => {
      refetchBnplStatus();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [user]);

   useEffect(() => {
      if (user) {
         setTamara(user.tamara_active ?? false);
         setTabby(user.tabby_active ?? false);
      }
   }, [user]);

   interface MenuLinkProps {
      name: any;
      img: string;
      redirectTo: string;
      hasTag?: boolean;
      onClickMethod?: () => any;
      hideForUnderReview?: boolean;
      is_external?: boolean;
   }

   const links: MenuLinkProps[] = [
      // {
      //    img: '/images/profile/person.svg',
      //    redirectTo: `/${lang}/profile/personal-info`,
      //    name: <Translate id="profile:personal info" />,
      // },
      // {
      //    img: '/images/profile/bag.svg',
      //    redirectTo: `/${lang}/profile/business/details`,
      //    name: <Translate id="profile:business info" />,
      // },
      {
         img: '/images/profile/doc.svg',
         redirectTo: user?.status === 'Under Review' ? '' : `/${lang}/profile/business/files`,
         name: <Translate id="profile:legal documents" />,
         hasTag: true,
      },
      // {
      //    img: '/images/profile/money.svg',
      //    redirectTo: `/${lang}/profile/business/billing-info`,
      //    name: <Translate id="profile:billing info" />,
      // },
      // {
      //    img: '/images/profile/paper.svg',
      //    redirectTo: `/${lang}/profile/business/payout`,
      //    name: <Translate id="payout:payout requests" />,
      // },
      // {
      // {
      //    img: '/images/profile/profile-card-icon.svg',
      //    redirectTo: `/${lang}/roles-and-permissions`,
      //    name: <Translate id="roles:user roles and permissions" />,
      // },
      // {
      //    img: '/images/profile/question.svg',
      //    redirectTo: `/${lang}/faq`,
      //    name: <Translate id="common:faq" />,
      // },
      {
         img: '/images/profile/api-icon.svg',
         redirectTo: `/${lang}/api`,
         name: <Translate id="api:partner integration" />,
         hideForUnderReview: true,
      },
      {
         img: '/images/profile/contact-support.svg',
         redirectTo: `https://sta.gov.sa/${lang}/contact-us/`,
         name: <Translate id="dashboard:contact support" />,
         is_external: true,
      },
      // {
      //    img: '/images/profile/toggle-arrows-icon.svg',
      //    redirectTo: '',
      //    name: (
      //       <Translate
      //          id={`onboard:${
      //             user?.is_umrah ? 'offer tourism packages' : 'offer umrah packages only'
      //          }`}
      //       />
      //    ),
      //    onClickMethod: () => setOpenProfileTypeModal(true),
      // },
   ];

   if (user?.status !== 'Under Review' && user?.is_umrah) {
      links.push({
         img: '/images/profile/toggle-arrows-icon.svg',
         redirectTo: '',
         name: <Translate id="onboard:offer tourism packages" />,
         onClickMethod: () => setOpenProfileTypeModal(true),
      });
   }

   if (bnplStatusData?.bnplSupport && !user?.international) {
      links.splice(2, 0, {
         img: '/images/profile/bnpl-icon.svg',
         redirectTo: '',
         name: <Translate id="bnpl:bnpl menu title" />,
         onClickMethod: () => setShowBnplPopup(true),
      });
   }

   const internationalLinks: MenuLinkProps[] = [
      {
         img: '/images/profile/doc.svg',
         redirectTo: `/${lang}/profile/business/files`,
         name: <Translate id="profile:legal documents" />,
         hasTag: true,
      },
      {
         img: '/images/profile/contact-support.svg',
         redirectTo: `https://sta.gov.sa/${lang}/contact-us/`,
         name: <Translate id="dashboard:contact support" />,
         is_external: true,
      },
   ];

   const {
      data: notificationList,
      isLoading,
      refetch: refetchNotifications,
   } = useNotificationList({
      pageNumber: 1,
   });
   useEffect(() => {
      if (user?.current_user?.unread_notifications_count) {
         refetchNotifications();
      }
   }, [refetchNotifications, user?.current_user?.unread_notifications_count]);

   const profileLinks = user?.international ? internationalLinks : links;

   // const { data: bookings } = useBookingsList();
   const profileStrength = user?.profile_strength || {};
   const verifiedBusinessProfile = profileStrength?.verified_business_profile;
   const isMobile = useMedia(1100);
   const { status } = useAccountStatus();
   const MenuLink: FC<MenuLinkProps> = ({
      redirectTo,
      name,
      img,
      hasTag,
      onClickMethod,
      is_external,
   }) => {
      const Tag =
         onClickMethod || (!redirectTo && !onClickMethod) ? 'div' : is_external ? 'a' : Link;
      return (
         <Tag
            href={redirectTo}
            target="_blank"
            rel="noreferrer"
            onClick={e => {
               if (onClickMethod) {
                  e.stopPropagation();
                  e.preventDefault();
                  onClickMethod();
               }
            }}>
            <a
               className={`flex items-center justify-between w-full text-gray-600 hover:bg-gray-100 focus:hover:bg-gray-100 h-11 ${
                  !redirectTo && !onClickMethod ? 'cursor-not-allowed' : 'cursor-pointer'
               }`}>
               <Menu.Button
                  className={`flex justify-between w-full items-center ${
                     !redirectTo && !onClickMethod ? 'cursor-not-allowed' : 'cursor-pointer'
                  }`}>
                  <div className="flex">
                     <img width="15" height="15" className="mx-3" src={img} />
                     <span>{name}</span>
                  </div>
                  {hasTag && status === 'Under Review' ? (
                     <div className="ltr:ml-1.5 rtl:mr-1.5 whitespace-nowrap font-semibold px-2 max-w-min py-0.5 uppercase bg-light-orange text-white text-[10px] rounded-full">
                        <div>
                           <Translate id="dashboard:under review" />
                        </div>
                     </div>
                  ) : null}
                  {!redirectTo && !onClickMethod ? (
                     <div></div>
                  ) : (
                     <Arrow direction="right" className="w-2 h-3 mx-6" />
                  )}
               </Menu.Button>
            </a>
         </Tag>
      );
   };

   return (
      <div className="fixed inset-x-0 top-0 z-[100] flex items-center justify-between h-20  bg-white border-b border-grey-500 px-8">
         <div className="flex items-center gap-4">
            <button onClick={() => toggleMenu()} className="block lg:hidden">
               <MenuIcon className="w-4" />
            </button>
            <Link href={`/${lang}/dashboard`}>
               <a className="flex items-center justify-center ">
                  <img
                     alt=""
                     src={`/images/brands/sta-logo-color-${lang}.svg`}
                     width={isMobile ? '90px' : '118px'}
                  />
               </a>
            </Link>
         </div>

         <div className="flex gap-3">
            {user?.status === 'Active' && !user.international ? (
               <Button
                  className="!hidden md:!flex h-12 gap-3 group border-none bg-grey-600"
                  theme="outline-primary"
                  onClick={() => {
                     setShowQuickstart(true);
                  }}>
                  <>
                     <QuestionMarkIcon className="[&>*]:fill-primary group-hover:[&>*]:fill-white group-disabled:[&>*]:fill-grey-300" />
                     <Translate id="tutorial:quickstart guide" />
                  </>
               </Button>
            ) : null}
            <div className="flex items-center space-x-1 divide-x sm:space-x-3 rtl:space-x-reverse md:ltr:mr-0 md:rtl:ml-0 divide-grey-500 rtl:divide-x-reverse">
               <LangSelector black_icon />

               {/* <Link href={`/${lang}/contact-support`}>
               <a className="flex items-center justify-center w-10 h-10 mx-2 bg-gray-100 rounded-full">
                  <img alt="" src="/icons/header/question.svg" width="10px" height="17px" />
               </a>
            </Link> */}
               <Menu as="div">
                  <div className="relative">
                     <Menu.Button
                        disabled={isLoading || !user?.current_user}
                        className="flex items-center justify-center w-10 h-10 rounded-full ltr:ml-2 rtl:mr-2"
                        aria-label="Notification">
                        {/* <img alt="" src="/icons/header/notification.svg" width="16px" height="16px" /> */}
                        <RingbellIcon width="20px" height="20px" />

                        {user?.current_user?.unread_notifications_count > 0 && (
                           <div className="absolute z-10 top-1 p-1 rtl:left-1 ltr:right-1 bg-light-red font-semibold text-white text-[9px] items-center flex justify-center h-4 w-4 rounded-full border-white border-2">
                              <span className="animate-[ping_3s_infinite] absolute inline-flex h-full w-full rounded-full bg-light-red opacity-75"></span>
                              {user?.current_user?.unread_notifications_count}
                           </div>
                        )}
                     </Menu.Button>
                  </div>
                  <Menu.Items className="absolute px-4 mt-4 bg-white rounded-lg shadow-lg rtl:origin-top-left rtl:left-24 ltr:origin-top-right top-14 ltr:right-24 ring-opacity-5 focus:outline-none">
                     <div className="py-3 border-b">
                        <div className="font-bold">
                           <Translate id="notification:notification" />
                        </div>
                     </div>
                     {notificationList?.data?.length ? (
                        notificationList?.data.slice(0, 3).map((item: any) => (
                           <div key={item.id}>
                              <NotificationItem
                                 customClassName="!px-0 !py-4"
                                 notification={item}
                                 isMenuItem={true}
                              />
                           </div>
                        ))
                     ) : (
                        <div className="flex items-center justify-center gap-2 py-10 !text-[14px] text-grey-300">
                           <RingbellIcon
                              width="15px"
                              height="15px"
                              className="[&>*]:fill-grey-300"
                           />
                           <Translate id="notification:you have no new notifications" />
                        </div>
                     )}
                     <div className="flex justify-center font-semibold border-t text-primary">
                        <NextLink className="p-4 text-xs cursor-pointer" href="/notifications">
                           <Translate id="common:see all" />
                        </NextLink>
                     </div>
                  </Menu.Items>
               </Menu>

               <Menu as="div" className="flex items-center ">
                  <Menu.Button
                     className="flex items-center justify-center w-10 h-10 font-bold uppercase bg-gray-100 rounded-full ltr:ml-2 rtl:mr-2"
                     aria-label="Account">
                     {user?.current_user?.name[0]}
                  </Menu.Button>

                  <Menu.Items className="absolute mt-4 overflow-hidden bg-white rounded-lg shadow-lg rtl:origin-top-left rtl:left-12 ltr:origin-top-right top-14 ltr:right-12 w-80 ring-opacity-5 focus:outline-none">
                     <div className="flex px-4 py-3">
                        <div className="flex items-center justify-center w-10 h-10 font-bold uppercase bg-gray-100 rounded-full">
                           {user?.current_user?.name[0]}
                        </div>
                        <div
                           className={cn(
                              'mx-3 ltr:text-left rtl:text-right',
                              !verifiedBusinessProfile && 'mt-2'
                           )}>
                           <div className="text-sm font-bold">
                              {verifiedBusinessProfile ? (
                                 user?.current_user?.name
                              ) : (
                                 <Translate id="profile:sta partner" />
                              )}
                           </div>
                           {(verifiedBusinessProfile || user?.status === 'Requires Update') && (
                              <a
                                 href={`${process.env.NEXT_PUBLIC_SSID_URL}`}
                                 target="_blank"
                                 rel="noopener noreferrer"
                                 className="text-xs text-primary">
                                 <Translate id="profile:view profile" />
                              </a>
                              // <NextLink
                              //    href={'`/profile/personal-info`'}
                              //    className="text-xs text-primary">
                              //    <Translate id="profile:view profile" />
                              // </NextLink>
                           )}
                        </div>
                     </div>
                     {(verifiedBusinessProfile || user?.status === 'Requires Update') &&
                        profileLinks
                           ?.filter(
                              (item: any) =>
                                 !(user.status === 'Under Review' && item.hideForUnderReview)
                           )
                           .map((item: any, idx: number) => (
                              <Menu.Item key={idx}>
                                 <MenuLink
                                    name={item.name}
                                    img={item.img}
                                    redirectTo={item.redirectTo}
                                    hasTag={item?.hasTag}
                                    onClickMethod={item?.onClickMethod}
                                    is_external={item?.is_external}
                                 />
                              </Menu.Item>
                           ))}
                     <Menu.Item>
                        <button
                           onClick={() => logout()}
                           className="flex items-center justify-between w-full text-gray-600 hover:bg-gray-100 focus:hover:bg-gray-100 h-11">
                           <div className="flex">
                              <img
                                 width="15"
                                 height="15"
                                 className="mx-3"
                                 src={'/images/profile/logout.svg'}
                              />
                              <span>
                                 <Translate id="common:logout" />
                              </span>
                           </div>
                           <Arrow direction="right" className="w-2 h-3 mx-6" />
                        </button>
                     </Menu.Item>
                  </Menu.Items>
               </Menu>
            </div>
         </div>
         <BnplSetupModal
            open={showBnplPopup}
            onClose={(showOtpForm?: boolean) => {
               setShowBnplPopup(false);
               if (showOtpForm) {
                  setShowVerifyPopup(true);
               }
            }}
            isBnplActive={bnplStatusData?.isBnplEnabled}
            tabbyServiceRate={bnplStatusData?.tabby_service_rate}
            tamaraServiceRate={bnplStatusData?.tamara_service_rate}
            tabby={tabby}
            setTabby={setTabby}
            tamara={tamara}
            setTamara={setTamara}
         />
         <BnplOtpModal
            open={showVerifyPopup}
            onClose={() => {
               setShowVerifyPopup(false);
               refetchBnplStatus();
            }}
            isBnplActive={bnplStatusData?.isBnplEnabled}
            tabby={tabby}
            tamara={tamara}
         />
         <QuickstartGuideModal open={showQuickstart} closeModal={() => setShowQuickstart(false)} />
         <UpdateProfileTypeModal
            open={openProfileTypeModal}
            setOpen={setOpenProfileTypeModal}
            refetchUser={() => {
               console.log('Refetch');
            }}
         />
      </div>
   );
};
